import { useSyncExternalStore } from "react";

/**
 * Ask Lilia on a figure (TikZ step 3, design-handoffs/2026-10-07-tikz-figures 1c).
 *
 * There is one app-level dock. Scoping it to a figure is shared state between
 * the dock (AskLiliaPanel), the figure on the canvas (FigureNodeView) and its
 * split view (TikzSplitView), which live in different React trees (the split
 * view is a portal, the dock a sibling of the editor). This module is that
 * state, and the pure rules the three of them follow: the context chip, the
 * suggestions by state, the line diff, the line chips.
 *
 * Nothing here talks to the server: the dock sends `figure` with its usual
 * POST /api/ai/ask, and the reply's `figure` proposal comes back compiled.
 */

// ── The API contract ────────────────────────────────────────────────────

export type FigureIntent = "draw" | "change" | "explain" | "caption" | "fix";

/** Sent with the message as `figure` (AskLiliaRequest). */
export interface TikzAskFigure {
  /** The TikZ figure; absent for a figure not inserted yet (Draw with Ask Lilia…). */
  blockId?: string;
  /** Where a new figure would go. */
  afterBlockId?: string;
  /** What the author sees now (the split view may be ahead of the save). */
  source?: string;
  caption?: string;
  label?: string;
  intent?: FigureIntent;
  /** The figure's error, for Fix this error. */
  error?: { line: number | null; message: string };
}

export interface TikzAskLine {
  from: number;
  to: number;
  text: string;
}

/** The reply's `figure`: what Ask Lilia proposes, compiled before it is shown. */
export interface TikzAskProposal {
  kind: "draw" | "change" | "failed" | "explain" | "caption" | "answer";
  source?: string;
  /** The SVG markup (draw and change: it drew). */
  svg?: string;
  drawMs?: number;
  attempts?: number;
  lineCount?: number;
  changedLines?: number;
  preambleAdditions?: string[];
  packages?: string[];
  error?: { kind: string; message: string; line?: number | null; excerpt?: string | null };
  caption?: string;
  lines?: TikzAskLine[];
}

/** Lock reasons the figure path adds to the dock's. They are messages, not a locked dock. */
export const FIGURE_LOCK_REASONS = ["no-access", "read-only", "not-a-figure", "no-document", "unavailable"] as const;

// ── Shared state ────────────────────────────────────────────────────────

export type FigureState = "empty" | "drawn" | "error";

/** What a TikZ figure on the canvas (or in its split view) publishes about itself. */
export interface FigureLive {
  documentId: string;
  blockId: string;
  /** "Figure 3" for a numbered figure, "Figure" for a bare picture. */
  name: string;
  source: string;
  caption: string;
  label: string;
  state: FigureState;
  error: { line: number | null; message: string } | null;
  splitOpen: boolean;
}

/** The figure the dock is scoped to. A new figure has no block yet. */
export interface FigureScope {
  documentId: string;
  blockId: string | null;
  afterBlockId: string | null;
  /** "3.2": the section a new figure would go in. */
  section: string | null;
  /** Who set it: the split view clears its own scope when it closes, not another's. */
  owner: "split" | "menu" | "insert" | "selection";
}

/** A change waiting in the split view for Keep or Discard. */
export interface PendingChange {
  blockId: string;
  before: string;
  source: string;
  /** A data: URL of the drawing; null when it didn't draw. */
  svg: string | null;
  drew: boolean;
  drawMs: number | null;
  changedLines: number;
  preambleAdditions: string[];
  error: { line: number | null; message: string } | null;
}

export interface LineHighlight {
  blockId: string;
  from: number;
  to: number;
}

interface TikzAskState {
  scope: FigureScope | null;
  figures: Readonly<Record<string, FigureLive>>;
  pending: Readonly<Record<string, PendingChange>>;
  highlight: LineHighlight | null;
}

let state: TikzAskState = { scope: null, figures: {}, pending: {}, highlight: null };
const listeners = new Set<() => void>();

function set(next: Partial<TikzAskState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

export function getTikzAskState(): TikzAskState {
  return state;
}

export function subscribeTikzAsk(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Read part of the state; the selector must return something stable between changes. */
export function useTikzAsk<T>(selector: (s: TikzAskState) => T): T {
  return useSyncExternalStore(subscribeTikzAsk, () => selector(state), () => selector(state));
}

export function setFigureScope(scope: FigureScope): void {
  set({ scope });
}

/** Clears the scope; with `owner`, only a scope that owner set. */
export function clearFigureScope(owner?: FigureScope["owner"], blockId?: string | null): void {
  const s = state.scope;
  if (!s) return;
  if (owner && (s.owner !== owner || (blockId !== undefined && s.blockId !== blockId))) return;
  set({ scope: null });
}

function sameLive(a: FigureLive | undefined, b: FigureLive): boolean {
  if (!a) return false;
  return (
    a.documentId === b.documentId &&
    a.name === b.name &&
    a.source === b.source &&
    a.caption === b.caption &&
    a.label === b.label &&
    a.state === b.state &&
    a.splitOpen === b.splitOpen &&
    (a.error?.line ?? null) === (b.error?.line ?? null) &&
    (a.error?.message ?? null) === (b.error?.message ?? null)
  );
}

export function publishFigure(live: FigureLive): void {
  if (sameLive(state.figures[live.blockId], live)) return;
  set({ figures: { ...state.figures, [live.blockId]: live } });
}

export function unpublishFigure(blockId: string): void {
  if (!state.figures[blockId]) return;
  const rest = { ...state.figures };
  delete rest[blockId];
  set({ figures: rest });
}

export function setPendingChange(change: PendingChange): void {
  set({ pending: { ...state.pending, [change.blockId]: change } });
}

export function clearPendingChange(blockId: string): void {
  if (!state.pending[blockId]) return;
  const rest = { ...state.pending };
  delete rest[blockId];
  set({ pending: rest });
}

export function setLineHighlight(h: LineHighlight | null): void {
  const was = state.highlight;
  if (was === h || (was && h && was.blockId === h.blockId && was.from === h.from && was.to === h.to)) return;
  set({ highlight: h });
}

/** For tests: back to nothing. */
export function resetTikzAsk(): void {
  state = { scope: null, figures: {}, pending: {}, highlight: null };
  listeners.forEach((l) => l());
}

// ── Asking a figure to do something ───────────────────────────────────

export const TIKZ_FIGURE_ACTION_EVENT = "lilia:tikz-figure-action";

export type FigureAction =
  | { type: "open-split" }
  | { type: "scroll-to-line"; line: number }
  | { type: "set-caption"; caption: string }
  | { type: "set-source"; source: string };

export interface FigureActionDetail {
  blockId: string;
  action: FigureAction;
}

/** The figure node with this block id does it (FigureNodeView listens). */
export function requestFigureAction(blockId: string, action: FigureAction): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<FigureActionDetail>(TIKZ_FIGURE_ACTION_EVENT, { detail: { blockId, action } }));
}

/** Insert → Figure → Draw with Ask Lilia…: the editor works out where, then scopes the dock. */
export const TIKZ_DRAW_WITH_ASK_EVENT = "lilia:tikz-draw-with-ask";

export function requestDrawWithAsk(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TIKZ_DRAW_WITH_ASK_EVENT));
}

// ── The dock's chip and suggestions ───────────────────────────────────

/** "Figure 3 · TikZ", or "New figure · §3.2". */
export function contextChipLabel(scope: FigureScope, live: FigureLive | undefined): string {
  if (scope.blockId) return `${live?.name ?? "Figure"} · TikZ`;
  return scope.section ? `New figure · §${scope.section}` : "New figure";
}

export interface FigureSuggestion {
  intent: FigureIntent;
  label: string;
  hint: string;
  /** What goes in the input. Nothing is sent until the author sends it. */
  prefill: string;
}

const SUGGEST: Record<FigureIntent, FigureSuggestion> = {
  draw: { intent: "draw", label: "Draw…", hint: "Describe the figure; it compiles before you see it.", prefill: "Draw " },
  change: {
    intent: "change",
    label: "Change…",
    hint: "Describe the change: \"make f and g dashed\", \"add an arrow from C back to A\".",
    prefill: "Change this figure: ",
  },
  explain: { intent: "explain", label: "Explain this figure", hint: "What it shows, line by line. Never edits.", prefill: "Explain this figure" },
  caption: {
    intent: "caption",
    label: "Write a caption",
    hint: "Suggests a caption from the drawing and the paragraph around it.",
    prefill: "Write a caption for this figure",
  },
  fix: {
    intent: "fix",
    label: "Fix this error",
    hint: "Offered only while the figure doesn't draw. The fix arrives as a diff, like any change.",
    prefill: "Fix this error",
  },
};

/** Empty: Draw… · Drawn: Change… · Explain · Write a caption · Error: Fix this error first. */
export function figureSuggestions(s: FigureState | null): FigureSuggestion[] {
  if (s === null || s === "empty") return [SUGGEST.draw];
  if (s === "error") return [SUGGEST.fix, SUGGEST.change, SUGGEST.explain];
  return [SUGGEST.change, SUGGEST.explain, SUGGEST.caption];
}

export function suggestionFor(intent: FigureIntent): FigureSuggestion {
  return SUGGEST[intent];
}

/**
 * The intent a sent message carries: a suggestion's, while the message still
 * starts with what that suggestion put in the input (the ⋯ menu's items fill
 * the same text); else none: a typed message, which the server reads for
 * itself. An empty figure is always drawn.
 */
export function intentFor(message: string, figureState: FigureState | null): FigureIntent | undefined {
  if (figureState === null || figureState === "empty") return "draw";
  const text = message.trim();
  for (const s of Object.values(SUGGEST)) {
    if (s.intent === "draw") continue;
    if (text.startsWith(s.prefill.trim())) return s.intent;
  }
  return undefined;
}

export function buildFigureRequest(scope: FigureScope, live: FigureLive | undefined, intent: FigureIntent | undefined): TikzAskFigure {
  if (!scope.blockId) return { afterBlockId: scope.afterBlockId ?? undefined, intent: "draw" };
  return {
    blockId: scope.blockId,
    source: live?.source,
    caption: live?.caption,
    label: live?.label,
    intent,
    error: intent === "fix" && live?.error ? live.error : undefined,
  };
}

// ── Lines ───────────────────────────────────────────────────────────────

/** "Line 7", "Lines 2–4". */
export function lineChipLabel(from: number, to: number): string {
  return from === to ? `Line ${from}` : `Lines ${from}–${to}`;
}

export interface DiffRow {
  op: " " | "-" | "+";
  text: string;
  /** The line's number in the new source (kept and added lines); null for a removed line. */
  n: number | null;
}

function splitLines(text: string): string[] {
  const t = text.replace(/\r\n/g, "\n").replace(/\n$/, "");
  return t.length ? t.split("\n") : [];
}

/** A line diff (longest common subsequence), removed lines before added ones in each hunk. */
export function lineDiff(before: string, after: string): DiffRow[] {
  const a = splitLines(before);
  const b = splitLines(after);
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--) lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
  const rows: DiffRow[] = [];
  let x = 0;
  let y = 0;
  while (x < n || y < m) {
    if (x < n && y < m && a[x] === b[y]) {
      rows.push({ op: " ", text: a[x], n: y + 1 });
      x++;
      y++;
      continue;
    }
    const removed: string[] = [];
    const added: DiffRow[] = [];
    while ((x < n || y < m) && !(x < n && y < m && a[x] === b[y])) {
      if (y >= m || (x < n && lcs[x + 1][y] >= lcs[x][y + 1])) removed.push(a[x++]);
      else {
        added.push({ op: "+", text: b[y], n: y + 1 });
        y++;
      }
    }
    for (const r of removed) rows.push({ op: "-", text: r, n: null });
    rows.push(...added);
  }
  return rows;
}

/** Per hunk, the larger of removed and added. */
export function changedLineCount(rows: DiffRow[]): number {
  let total = 0;
  let removed = 0;
  let added = 0;
  for (const r of [...rows, { op: " " as const, text: "", n: null }]) {
    if (r.op === "-") removed++;
    else if (r.op === "+") added++;
    else {
      total += Math.max(removed, added);
      removed = 0;
      added = 0;
    }
  }
  return total;
}

/** The added lines' numbers, as ranges ("Lines 5–6" chips for a change). */
export function addedRanges(rows: DiffRow[]): { from: number; to: number }[] {
  const out: { from: number; to: number }[] = [];
  for (const r of rows) {
    if (r.op !== "+" || r.n === null) continue;
    const last = out[out.length - 1];
    if (last && last.to === r.n - 1) last.to = r.n;
    else out.push({ from: r.n, to: r.n });
  }
  return out;
}

/** "Ask Lilia changed 3 lines." */
export function changedSentence(n: number): string {
  return n === 1 ? "Ask Lilia changed 1 line." : `Ask Lilia changed ${n} lines.`;
}

// ── Where a figure is ─────────────────────────────────────────────────

/** A top-level node of the editor's document, as much as numbering needs. */
export interface OutlineNode {
  type: string;
  blockId?: string | null;
  level?: number | null;
  /** Figures: a caption numbers a figure; a bare picture has none. */
  caption?: string | null;
}

/** "Figure 3": numbered figures (a caption) count, bare pictures don't. */
export function figureName(nodes: OutlineNode[], blockId: string): string {
  let n = 0;
  for (const node of nodes) {
    const numbered = node.type === "figureBlock" && !!node.caption?.trim();
    if (numbered) n++;
    if (node.blockId === blockId) return numbered ? `Figure ${n}` : "Figure";
  }
  return "Figure";
}

/** "3.2": the heading numbers in force after the node at `index` (null before any heading). */
export function sectionAt(nodes: OutlineNode[], index: number): string | null {
  const counters: number[] = [];
  for (let i = 0; i <= index && i < nodes.length; i++) {
    const node = nodes[i];
    if (node.type !== "heading") continue;
    const level = Math.max(1, Math.min(6, node.level ?? 1));
    counters.length = level;
    for (let k = 0; k < level; k++) counters[k] = counters[k] ?? 0;
    counters[level - 1]++;
  }
  return counters.length ? counters.map((c) => c || 1).join(".") : null;
}

/** The part of a ProseMirror node numbering reads. */
interface OutlineSourceNode {
  type: { name: string };
  attrs: Record<string, unknown>;
}

/** The editor document's top-level nodes, as numbering reads them. */
export function outlineOf(doc: { forEach?: (f: (node: OutlineSourceNode, offset: number, index: number) => void) => void } | null | undefined): OutlineNode[] {
  const nodes: OutlineNode[] = [];
  if (typeof doc?.forEach !== "function") return nodes;
  doc.forEach((node) => {
    nodes.push({
      type: node.type.name,
      blockId: typeof node.attrs.blockId === "string" ? node.attrs.blockId : null,
      level: typeof node.attrs.level === "number" ? node.attrs.level : null,
      caption: typeof node.attrs.caption === "string" ? node.attrs.caption : null,
    });
  });
  return nodes;
}
